import { Checkbox, TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import * as React from "react";
import TypographyCompo from "../../components/Comman/TypographyCompo";
import { lightRedPaleBgV2, outOfStockColor, redColor } from "../../config/ColorObj";
import {
	getColumnAlign,
	getColumnHeaderAlign,
	getColumnHeaderClassName,
	getColumnHeaderLines,
	getColumnHeaderSx,
	getColumnHeaderTitle,
	getColumnInputStyle,
	getColumnWidthSx,
} from "./outletInvoicingColumnConfig";

const OUT_OF_STOCK_ROW_BG = outOfStockColor;

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator(order, orderBy) {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

const getStockClass = (value) => {
	const num = Number(value);
	if (num === 0) return "stock-zero";
	if (num > 0 && num <= 5) return "stock-low";
	return "";
};

const getRowClassName = (row, flag, enhancedLayout) => {
	if (enhancedLayout && flag && !row.checked) return "row-out-of-stock";
	return "";
};

const getCellBg = (row, flag, enhancedLayout) => {
	if (!flag || row.checked) return {};
	return  { backgroundColor: OUT_OF_STOCK_ROW_BG };
};

function WidthCell({
	columnKey,
	columnDef = {},
	children,
	cellStyle = {},
	className = "",
	sx = {},
}) {
	return (
		<TableCell
			align={getColumnAlign(columnKey, columnDef)}
			className={className}
			sx={{ ...getColumnWidthSx(columnKey, columnDef), ...sx }}
			style={cellStyle}
		>
			{children}
		</TableCell>
	);
}

function ColumnHeaderLabel({ columnKey, columnDef }) {
	const lines = getColumnHeaderLines(columnKey, columnDef);

	if (lines) {
		return (
			<Box component="span" className="header-wrap-label">
				{lines.map((line, index) => (
					<React.Fragment key={index}>
						{index > 0 && <br />}
						{line}
					</React.Fragment>
				))}
			</Box>
		);
	}

	return getColumnHeaderTitle(columnKey, columnDef);
}

function EnhancedTableHead(props) {
	const {
		onSelectAllClick,
		headCells,
		order,
		orderBy,
		numSelected,
		rowCount,
		onRequestSort,
		isCheckBox = true,
		enhancedLayout = false,
	} = props;

	if (enhancedLayout) {
		return (
			<TableHead>
				<TableRow>
					{isCheckBox && (
						<TableCell
							padding="checkbox"
							align="center"
							sx={getColumnHeaderSx("checkbox")}
						/>
					)}
					{headCells.map((headCell) => (
						<TableCell
							key={headCell.name || headCell.id}
							align={getColumnHeaderAlign(headCell.name, headCell)}
							className={getColumnHeaderClassName(headCell.name, headCell)}
							sx={getColumnHeaderSx(headCell.name, headCell)}
						>
							<ColumnHeaderLabel
								columnKey={headCell.name}
								columnDef={headCell}
							/>
							{orderBy === (headCell.name || headCell.id) ? (
								<Box component="span" sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableCell>
					))}
				</TableRow>
			</TableHead>
		);
	}

	return (
		<TableHead>
			<TableRow>
				{isCheckBox && (
					<>
						<TableCell></TableCell>
					</>
				)}

				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={"center"}
						width={headCell.minWidth}
					>
						{headCell.title}
						{orderBy === headCell.id ? (
							<Box component="span" sx={visuallyHidden}>
								{order === "desc" ? "sorted descending" : "sorted ascending"}
							</Box>
						) : null}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	numSelected: PropTypes.number.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	onSelectAllClick: PropTypes.func.isRequired,
	order: PropTypes.oneOf(["asc", "desc"]).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
};

export default function EnhancedTable({
	rows,
	columns,
	stockHolderAndProductConfiguration,
	selection,
	onSelectionChange,
	disabled = false,
	isCheckBox = true,
	setList,
	flag,
	freeQtyFlag,
	newRowData,
	enhancedLayout = false,
	emptyMessage = "No Record Found",
}) {
	const [order, setOrder] = React.useState("asc");
	const [orderBy, setOrderBy] = React.useState("calories");
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSelectAllClick = (event) => {
		if (event.target.checked) {
			if (disabled) {
				let newSelecteds = [];
				rows.map((n) => {
					if (!n.disabled) {
						newSelecteds.push(n.id);
					}
				});
				onSelectionChange(newSelecteds);
			} else {
				const newSelecteds = rows.map((n) => n.id);
				onSelectionChange(newSelecteds);
			}

			return;
		}
		onSelectionChange([]);
	};

	const handleClick = (event, name) => {
		const selectedIndex = selection.indexOf(name);
		let newSelected = [];
		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selection, name);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selection.slice(1));
		} else if (selectedIndex === selection.length - 1) {
			newSelected = newSelected.concat(selection.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selection.slice(0, selectedIndex),
				selection.slice(selectedIndex + 1),
			);
		}

		onSelectionChange(newSelected);
	};

	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const paginatedRows =
		rowsPerPage > 0
			? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			: rows;

	const pagination = (
		<TablePagination
			rowsPerPageOptions={[25, 50, 75, 100]}
			colSpan={enhancedLayout ? undefined : 15}
			count={rows.length}
			rowsPerPage={rowsPerPage}
			page={page}
			component={enhancedLayout ? "div" : undefined}
			sx={
				enhancedLayout
					? undefined
					: { justifyContent: "flex-end", display: "flex", border: 0 }
			}
			slotProps={{
				select: {
					inputProps: {
						"aria-label": "rows per page",
					},
					native: true,
				},
			}}
			onPageChange={handleChangePage}
			onRowsPerPageChange={handleChangeRowsPerPage}
		/>
	);

	return (
		<Box
			sx={{ width: "100%", border: 0 }}
			className={enhancedLayout ? "outlet-invoicing-table-wrapper" : undefined}
		>
			<TableContainer
				sx={enhancedLayout ? undefined : { maxHeight: 500 }}
				className={
					enhancedLayout
						? `outlet-invoicing-table-container${
								rows.length === 0
									? " outlet-invoicing-table-container--empty"
									: ""
							}`
						: undefined
				}
			>
				<Table
					stickyHeader={enhancedLayout}
					className={enhancedLayout ? "outlet-invoicing-table" : undefined}
					sx={{ minWidth: 500 }}
					aria-label={
						enhancedLayout
							? "outlet invoicing table"
							: "custom pagination table"
					}
				>
					<EnhancedTableHead
						headCells={columns}
						numSelected={selection.length}
						order={order}
						orderBy={orderBy}
						onSelectAllClick={handleSelectAllClick}
						onRequestSort={handleRequestSort}
						rowCount={rows.length}
						isCheckBox={isCheckBox}
						enhancedLayout={enhancedLayout}
					/>
					<TableBody>
						{paginatedRows.map((row, index) => {
							const isItemSelected = selection.indexOf(row.id) !== -1;
							const labelId = `enhanced-table-checkbox-${index}`;

							return (
								<Row
									key={row.id || index}
									tableData={rows}
									setList={setList}
									row={row}
									stockHolderAndProductConfiguration={
										stockHolderAndProductConfiguration
									}
									handleClick={handleClick}
									labelId={labelId}
									isItemSelected={isItemSelected}
									isCheckBox={isCheckBox}
									flag={flag}
									freeQtyFlag={freeQtyFlag}
									newRowData={newRowData}
									enhancedLayout={enhancedLayout}
								/>
							);
						})}
						{emptyRows > 0 && (
							<TableRow
								style={{
									height: 53 * emptyRows,
								}}
							>
								<TableCell colSpan={columns.length + 1} />
							</TableRow>
						)}
						{rows.length === 0 && (
							<TableRow
								style={
									enhancedLayout
										? undefined
										: {
												height: 53 * 1,
											}
								}
							>
								<TableCell align="center" colSpan={columns.length + 1}>
									{enhancedLayout ? (
										<Box className="outlet-invoicing-empty">{emptyMessage}</Box>
									) : (
										emptyMessage
									)}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{enhancedLayout ? (
				<Box className="outlet-invoicing-pagination">{pagination}</Box>
			) : (
				<TableFooter
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "flex-end",
						border: 0,
					}}
				>
					<TableRow sx={{ alignItems: "flex-end", display: "flex", border: 0 }}>
						{pagination}
					</TableRow>
				</TableFooter>
			)}
		</Box>
	);
}

function Row({
	tableData,
	row,
	labelId,
	isItemSelected,
	handleClick,
	setList,
	isCheckBox = true,
	flag,
	stockHolderAndProductConfiguration,
	freeQtyFlag,
	newRowData,
	enhancedLayout = false,
}) {
	const onChangeTextField = (e, row, name) => {
		const { value } = e.target;
		const rowList = newRowData.map((rowData) => {
			return {
				...rowData,
				[name]: row.id == rowData.id ? value : rowData[name],
			};
		});
		setList({ row: rowList });
	};

	const cellStyle = getCellBg(row, flag, enhancedLayout);
	const rowClassName = getRowClassName(row, flag, enhancedLayout);
	const inputClassName = enhancedLayout
		? "outlet-invoicing-input"
		: "customTextfield";

	const isRateEditable =
		(stockHolderAndProductConfiguration.rateEditOrNot
			.stockHolderAndProductConfiguration &&
			stockHolderAndProductConfiguration.rateEditOrNot.stockHolderAndProductConfiguration.filter(
				(rowData) =>
					rowData.product && row.product && rowData.product.id == row.product,
			).length != 0) ||
		(localStorage.getItem("isRateEditableForHoreca") == 1 &&
			row.additionalRateApplicable == 0);

	if (enhancedLayout) {
		return (
			<TableRow
				hover
				role="checkbox"
				aria-checked={isItemSelected}
				tabIndex={-1}
				selected={isItemSelected}
				className={rowClassName}
			>
				{isCheckBox && (
					<TableCell
						padding="checkbox"
						align="center"
						sx={getColumnWidthSx("checkbox")}
						style={cellStyle}
					>
						<Checkbox
							color="primary"
							onClick={(event) => handleClick(event, row.id)}
							checked={row.checked}
							disabled={flag ? true : false}
							inputProps={{
								"aria-labelledby": labelId,
							}}
						/>
					</TableCell>
				)}

				<WidthCell columnKey="index" cellStyle={cellStyle}>
					{row.index}
				</WidthCell>
				<WidthCell columnKey="firmName" cellStyle={cellStyle}>
					{row.firmName}
				</WidthCell>
				<WidthCell columnKey="beatName" cellStyle={cellStyle}>
					{row.beatName}
				</WidthCell>
				<WidthCell columnKey="orderNo" cellStyle={cellStyle}>
					{row.orderNo}
				</WidthCell>
				<WidthCell columnKey="orderDate" cellStyle={cellStyle}>
					{row.orderDate}
				</WidthCell>
				<WidthCell columnKey="productName" cellStyle={cellStyle}>
					<TypographyCompo className="cell-product">
						{row.productName}
					</TypographyCompo>
				</WidthCell>
				<WidthCell columnKey="outletUom" cellStyle={cellStyle}>
					{row.outletUom}
				</WidthCell>

				{isRateEditable ? (
					<WidthCell columnKey="rate" cellStyle={cellStyle}>
						<input
							className={inputClassName}
							placeholder="Rate"
							value={row.rate}
							name="rate"
							onChange={(e) => {
								onChangeTextField(e, row, "rate");
							}}
							type="text"
							id="rate"
							style={getColumnInputStyle("rate")}
						/>
						{row.isRateWiseError && (
							<TypographyCompo
								sx={{ color: redColor, fontSize: "11px", display: "block" }}
							>
								Invalid rate
							</TypographyCompo>
						)}
					</WidthCell>
				) : (
					<WidthCell columnKey="rate" cellStyle={cellStyle}>
						{row.rate}
					</WidthCell>
				)}

				{flag && (
					<>
						<WidthCell columnKey="secondaryClosingQty" cellStyle={cellStyle}>
							<TypographyCompo
								className={getStockClass(row.secondaryClosingQty)}
							>
								{row.secondaryClosingQty}
							</TypographyCompo>
						</WidthCell>
						<WidthCell columnKey="productStock" cellStyle={cellStyle}>
							<TypographyCompo className={getStockClass(row.productStock)}>
								{row.productStock}
							</TypographyCompo>
						</WidthCell>
					</>
				)}

				<WidthCell columnKey="billQty" cellStyle={cellStyle}>
					{row.billQty}
				</WidthCell>
				<WidthCell columnKey="orderQty" cellStyle={cellStyle}>
					<input
						className={inputClassName}
						placeholder="Quantity"
						value={row.orderQty}
						name="orderQty"
						onChange={(e) => {
							onChangeTextField(e, row, "orderQty");
						}}
						type="text"
						id="orderQty"
						disabled={true}
						style={getColumnInputStyle("orderQty")}
					/>
				</WidthCell>

				<WidthCell columnKey="total" cellStyle={cellStyle}>
					<TypographyCompo className="cell-total">{row.total}</TypographyCompo>
				</WidthCell>
			</TableRow>
		);
	}

	return (
		<TableRow
			hover
			role="checkbox"
			aria-checked={isItemSelected}
			tabIndex={-1}
			key={row.name}
			selected={isItemSelected}
		>
			{isCheckBox && (
				<TableCell
					padding="checkbox"
					style={{
						backgroundColor: !flag
							? ""
							: row.checked
								? ""
								: OUT_OF_STOCK_ROW_BG,
					}}
				>
					<Checkbox
						color="primary"
						onClick={(event) => handleClick(event, row.id)}
						checked={row.checked}
						disabled={flag ? true : false}
						inputProps={{
							"aria-labelledby": labelId,
						}}
					/>
				</TableCell>
			)}

			<TableCell
				align="center"
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.index}
			</TableCell>
			<TableCell
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.firmName}
			</TableCell>
			<TableCell
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.beatName}
			</TableCell>
			<TableCell
				padding="checkbox"
				align="center"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.orderNo}
			</TableCell>
			<TableCell
				padding="checkbox"
				sx={{ width: "8%" }}
				align="center"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.orderDate}
			</TableCell>

			<TableCell
				padding="checkbox"
				sx={{ width: "8%" }}
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.productName}
			</TableCell>
			<TableCell
				align="center"
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.outletUom}
			</TableCell>
			{isRateEditable ? (
				<TableCell
					align="right"
					style={{
						width: "10%",
						backgroundColor: !flag
							? ""
							: row.checked
								? ""
								: OUT_OF_STOCK_ROW_BG,
					}}
				>
					<input
						className={inputClassName}
						placeHolder={"Rate"}
						value={row.rate}
						name="rate"
						onChange={(e) => {
							onChangeTextField(e, row, "rate");
						}}
						type="text"
						id="rate"
					></input>
					{row.isRateWiseError && (
						<span style={{ color: redColor, fontSize: "12px" }}>
							{"Invalid rate"}
						</span>
					)}
				</TableCell>
			) : (
				<TableCell
					style={{
						width: "10%",
						backgroundColor: !flag
							? ""
							: row.checked
								? ""
								: OUT_OF_STOCK_ROW_BG,
					}}
				>
					{row.rate}
				</TableCell>
			)}
			{flag && (
				<>
					<TableCell
						align="right"
						padding="checkbox"
						style={{
							backgroundColor: !flag
								? ""
								: row.checked
									? ""
									: OUT_OF_STOCK_ROW_BG,
						}}
					>
						{row.secondaryClosingQty}
					</TableCell>

					<TableCell
						align="right"
						padding="checkbox"
						style={{
							backgroundColor: !flag
								? ""
								: row.checked
									? ""
									: OUT_OF_STOCK_ROW_BG,
						}}
					>
						{row.productStock}
					</TableCell>
				</>
			)}
			<TableCell
				align="right"
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.billQty}
			</TableCell>
			<TableCell
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				<input
					className={inputClassName}
					placeHolder={"Quantity"}
					value={row.orderQty}
					name="orderQty"
					onChange={(e) => {
						onChangeTextField(e, row, "orderQty");
					}}
					type="text"
					id="orderQty"
					disabled={true}
					size="small"
					style={{ opacity: "60%" }}
				></input>
			</TableCell>

			<TableCell
				align="right"
				padding="checkbox"
				style={{
					backgroundColor: !flag ? "" : row.checked ? "" : OUT_OF_STOCK_ROW_BG,
				}}
			>
				{row.total}
			</TableCell>
		</TableRow>
	);
}
